import React from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { getMissionProgressDisplay } from '../../../../shared/onboarding/buildMissionTourSteps';
import { useLearningLabMission } from './LearningLabMissionProvider';
import { MissionChecklist } from './MissionChecklist';
import { MissionStepCard } from './MissionStepCard';
import { useMissionPresentation } from './useMissionPresentation';
import { TOUR_DOM_EVENTS } from '../../onboarding/onboardingDomEvents';
import { coerceEventListingPlainText } from '../../../../shared/utils/coerceEventListingText';
import {
  MISSION_PANEL_WIDTH_PX,
  MISSION_PANEL_INSET_RIGHT_PX,
  MISSION_PANEL_Z_INDEX,
} from '../../../../shared/onboarding/missionViewport';

export { MISSION_PANEL_WIDTH_PX, MISSION_PANEL_INSET_RIGHT_PX };

export const LearningLabMissionPanel: React.FC = () => {
  const { state, config } = useOnboarding();
  const mission = useLearningLabMission();
  const step = mission.currentMissionStep;
  const { variant, collapseSheet, panelWidth } = useMissionPresentation(
    step?.kind,
    step?.targetZone,
    mission.panelCollapsed,
  );
  const [sheetCollapsed, setSheetCollapsed] = React.useState(false);
  const [userExpandedPanel, setUserExpandedPanel] = React.useState(false);

  React.useEffect(() => {
    setSheetCollapsed(false);
    setUserExpandedPanel(false);
  }, [step?.id, state.currentStepIndex]);

  const autoCollapse = collapseSheet && !userExpandedPanel;
  const collapsed = mission.panelCollapsed || sheetCollapsed || autoCollapse;

  if (!state.missionMode || !state.isRunning || !mission.playbook) {
    return null;
  }

  const handleExpandPanel = () => {
    setUserExpandedPanel(true);
    setSheetCollapsed(false);
    mission.setPanelCollapsed(false);
    window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.MISSION_PANEL_EXPAND));
  };

  const handleCollapsePanel = () => {
    setUserExpandedPanel(false);
    if (variant === 'sheet') {
      setSheetCollapsed(true);
      return;
    }
    mission.setPanelCollapsed(true);
  };

  const tourStep = state.currentSteps[state.currentStepIndex];
  const missionProgress = getMissionProgressDisplay(
    state.currentSteps,
    state.currentStepIndex,
    mission.playbook,
  );

  const panelBody = (
    <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="overline" color="primary">
            Mission
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} noWrap={collapsed}>
            {collapsed
              ? coerceEventListingPlainText(step?.title ?? mission.playbook.title, 'Mission')
              : coerceEventListingPlainText(mission.playbook.title, 'Mission')}
          </Typography>
          {mission.targetOccluded && !collapsed ? (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              Target hidden — panel minimized or use Show me / fallback below.
            </Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          aria-label={collapsed ? 'Expand mission panel' : 'Collapse mission panel'}
          onClick={collapsed ? handleExpandPanel : handleCollapsePanel}
        >
          {collapsed ? <ExpandLessIcon /> : variant === 'sheet' ? <CloseIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>

      {!collapsed ? (
        <>
          <Box sx={{ flexShrink: 0 }}>
            {step && missionProgress ? (
              <MissionStepCard
                title={step.title}
                instruction={step.instruction}
                why={step.why}
                tip={step.tip}
                advanceGate={step.advanceGate}
                onContinue={mission.advanceMissionStep}
                onShowMe={mission.retryCurrentTarget}
                onFallback={mission.executeFallback}
                showFallback={mission.showFallbackAction}
                fallbackLabel={mission.fallbackLabel}
                targetOccluded={mission.targetOccluded}
                showManualNext={mission.showManualNext}
                routeNavigating={mission.routeNavigating}
                continueDisabled={!mission.routeReady}
                stepIndex={missionProgress.current - 1}
                totalSteps={missionProgress.total}
              />
            ) : tourStep ? (
              <MissionStepCard
                title={String(tourStep.title ?? 'Continue')}
                instruction={String(tourStep.content ?? '')}
                advanceGate={{ type: 'manual' }}
                onContinue={mission.advanceMissionStep}
                onShowMe={mission.retryCurrentTarget}
                stepIndex={0}
                totalSteps={1}
                showProgress={false}
              />
            ) : null}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mt: 1 }}>
            <MissionChecklist
              groups={mission.playbook.groups}
              rows={mission.checklistRows}
            />
            {config.allowSkip ? (
              <Button
                size="small"
                color="error"
                sx={{ mt: 1 }}
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent(TOUR_DOM_EVENTS.REQUEST_TOUR_SKIP),
                  )
                }
              >
                Skip mission
              </Button>
            ) : null}
          </Box>
        </>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          role="button"
          tabIndex={0}
          onClick={handleExpandPanel}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleExpandPanel();
            }
          }}
          sx={{ cursor: 'pointer' }}
        >
          {coerceEventListingPlainText(
            step?.title ?? mission.playbook.title,
            'Mission',
          )}{' '}
          — tap to expand
        </Typography>
      )}
    </Stack>
  );

  if (variant === 'sheet') {
    return (
      <Drawer
        anchor="bottom"
        variant="persistent"
        open
        PaperProps={{
          sx: {
            maxHeight: collapsed ? 72 : '45vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: (theme) => theme.zIndex.modal + MISSION_PANEL_Z_INDEX,
          },
        }}
        data-tour-stable="learning-lab-mission-panel"
      >
        {panelBody}
      </Drawer>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: 72,
        right: 16,
        width: panelWidth,
        maxHeight: collapsed ? 72 : 'calc(100vh - 96px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: collapsed ? 1.5 : 2,
        zIndex: (theme) => theme.zIndex.modal + MISSION_PANEL_Z_INDEX,
      }}
      data-tour-stable="learning-lab-mission-panel"
    >
      {panelBody}
    </Paper>
  );
};

export default LearningLabMissionPanel;
